import { sanitizeHtml } from './sanitizeHtml';

export class CursorManager {
  static saveCursorPosition(containerElement: HTMLElement): number {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && containerElement) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(containerElement);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      return preCaretRange.toString().length;
    }
    return 0;
  }

  static restoreCursorPosition(containerElement: HTMLElement, savedPosition: number): void {
    if (!containerElement) return;
    
    try {
      const selection = window.getSelection();
      if (!selection) return;

      // Validar se a posição é válida para o conteúdo atual
      const totalTextLength = containerElement.textContent?.length || 0;
      if (savedPosition > totalTextLength) {
        // Se posição é maior que o texto, colocar no final
        const range = document.createRange();
        range.selectNodeContents(containerElement);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }
      
      const range = document.createRange();
      let currentPosition = 0;
      
      const walker = document.createTreeWalker(
        containerElement,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let node;
      let positionSet = false;
      
      while (node = walker.nextNode()) {
        const nodeLength = node.textContent?.length || 0;
        if (currentPosition + nodeLength >= savedPosition) {
          const offsetInNode = Math.max(0, Math.min(savedPosition - currentPosition, nodeLength));
          range.setStart(node, offsetInNode);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          positionSet = true;
          break;
        }
        currentPosition += nodeLength;
      }
      
      // Fallback: se não conseguiu posicionar, coloca no final
      if (!positionSet && containerElement.childNodes.length > 0) {
        range.selectNodeContents(containerElement);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } catch (error) {
      console.warn('CursorManager: Error restoring cursor position, using fallback:', error);
      // Fallback robusto: posiciona no final
      try {
        const range = document.createRange();
        const selection = window.getSelection();
        if (selection) {
          range.selectNodeContents(containerElement);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } catch (fallbackError) {
        console.error('CursorManager: Fallback failed:', fallbackError);
      }
    }
  }

  static saveAndRestoreOperation<T>(
    containerElement: HTMLElement,
    operation: () => T
  ): T {
    console.log('CursorManager: Saving cursor position');
    const savedPosition = this.saveCursorPosition(containerElement);
    const result = operation();
    
    // Restore cursor position immediately
    console.log('CursorManager: Restoring cursor position');
    this.restoreCursorPosition(containerElement, savedPosition);
    
    return result;
  }

  static getCaretCoordinates(containerElement: HTMLElement): { x: number; y: number } | null {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    
    return {
      x: rect.left - containerRect.left,
      y: rect.top - containerRect.top
    };
  }

  static setCaretAtPoint(containerElement: HTMLElement, x: number, y: number): void {
    const containerRect = containerElement.getBoundingClientRect();
    const absoluteX = x + containerRect.left;
    const absoluteY = y + containerRect.top;
    
    const range = document.caretRangeFromPoint(absoluteX, absoluteY);
    if (range) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }

  static formatContentForDisplay(textContent: string): string {
    if (!textContent || textContent.trim().length === 0) return '';

    // 1) Se já é HTML, apenas devolve (evita reprocessar e quebrar tags)
    const looksLikeHTML = /<\/?[a-z][\s\S]*>/i.test(textContent);
    if (looksLikeHTML) return textContent;

    // 2) Texto puro → formata em parágrafos (mantendo quebras simples)
    const escape = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    return textContent
      .split(/\n{2,}/)                         // blocos por linha em branco
      .filter(p => p.trim().length > 0)
      .map(p => {
        const inner = escape(p).replace(/\n/g, '<br>');
        return `<p style="text-align:justify; text-indent:2cm; line-height:1.5; margin-bottom:1em; font-size:12pt;">${inner}</p>`;
      })
      .join('');
  }

  static extractPlainText(htmlContent: string): string {
    // Remove HTML tags and convert to plain text, maintaining line breaks
    return htmlContent
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<br[^>]*>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .trim();
  }

  // Função para inserir texto na posição do cursor ou no final do documento
  static insertTextAtCursor(containerElement: HTMLElement, textToInsert: string): void {
    if (!containerElement || !textToInsert) return;

    const selection = window.getSelection();
    
    // Se há seleção ativa, inserir na posição do cursor
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      // Criar nó de texto formatado e sanitizado
      const formattedText = this.formatContentForDisplay(textToInsert);
      const sanitizedText = sanitizeHtml(formattedText);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sanitizedText;
      
      // Inserir os nós na posição do cursor
      while (tempDiv.firstChild) {
        range.insertNode(tempDiv.firstChild);
        range.collapse(false); // Move cursor para após o texto inserido
      }
      
      // Limpar seleção e posicionar cursor
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // Se não há cursor, inserir no final do documento
      const formattedText = this.formatContentForDisplay(textToInsert);
      const sanitizedText = sanitizeHtml(formattedText);
      
      // Se documento está vazio, substituir conteúdo
      if (!containerElement.innerHTML.trim()) {
        containerElement.innerHTML = sanitizedText;
      } else {
        // Se há conteúdo, adicionar no final
        containerElement.innerHTML += sanitizedText;
      }
      
      // Posicionar cursor no final
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(containerElement);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }

  // Função para anexar texto ao final do documento (para streaming)
  static appendTextToDocument(containerElement: HTMLElement, textToAppend: string): void {
    if (!containerElement || !textToAppend) return;

    // Se documento está vazio, usar formatação completa
    if (!containerElement.innerHTML.trim()) {
      const formattedText = this.formatContentForDisplay(textToAppend);
      containerElement.innerHTML = sanitizeHtml(formattedText);
    } else {
      // Se há conteúdo, adicionar como novo parágrafo
      const formattedText = this.formatContentForDisplay(textToAppend);
      containerElement.innerHTML += sanitizeHtml(formattedText);
    }

    // Manter cursor no final durante streaming
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(containerElement);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  // Nova função para preservar formatação durante undo/redo
  static preserveFormattingOperation<T>(
    containerElement: HTMLElement,
    operation: () => T
  ): T {
    const savedPosition = this.saveCursorPosition(containerElement);
    const savedSelection = this.saveSelection();
    
    const result = operation();
    
    // Pequeno delay para permitir que o DOM se atualize
    setTimeout(() => {
      this.restoreCursorPosition(containerElement, savedPosition);
      if (savedSelection) {
        this.restoreSelection(savedSelection);
      }
    }, 10);
    
    return result;
  }

  private static saveSelection(): any {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    
    const range = selection.getRangeAt(0);
    return {
      startContainer: range.startContainer,
      startOffset: range.startOffset,
      endContainer: range.endContainer,
      endOffset: range.endOffset,
      collapsed: range.collapsed
    };
  }

  private static restoreSelection(savedSelection: any): void {
    if (!savedSelection) return;
    
    try {
      const selection = window.getSelection();
      const range = document.createRange();
      
      range.setStart(savedSelection.startContainer, savedSelection.startOffset);
      range.setEnd(savedSelection.endContainer, savedSelection.endOffset);
      
      if (savedSelection.collapsed) {
        range.collapse(true);
      }
      
      selection?.removeAllRanges();
      selection?.addRange(range);
    } catch (error) {
      console.warn('CursorManager: Error restoring selection:', error);
    }
  }
}